import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Download, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface PetImportData {
  name: string;
  species: string;
  breed?: string;
  color?: string;
  gender?: string;
  date_of_birth?: string;
  weight?: number;
  microchip_id?: string;
  spayed_neutered?: boolean;
  allergies?: string;
  medications?: string;
}

interface ImportRecord {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  tracking_code?: number;
  pet1?: PetImportData;
  pet2?: PetImportData;
  pet3?: PetImportData;
}

interface ParsedRow {
  rowNumber: number;
  data: ImportRecord;
  errors: string[];
  isValid: boolean;
}

interface ImportResultItem {
  row_number: number;
  name: string;
  success: boolean;
  pet_owner_id?: string;
  pets_created: number;
  error?: string;
}

interface ImportResult {
  total_records: number;
  successful: number;
  failed: number;
  results: ImportResultItem[];
}

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'results'>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setStep('upload');
    setParsedRows([]);
    setImportResult(null);
    setError(null);
    onClose();
  };

  // Normalize phone number to E.164 format for display
  const normalizePhone = (phone: string): string => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (digits.length >= 10) return `+${digits}`;
    return phone;
  };

  // Parse CSV text into rows
  const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    return lines.map(line => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    });
  };

  // Build pet data from row based on prefix (pet1_, pet2_, pet3_)
  const buildPetData = (row: Record<string, string>, prefix: string): PetImportData | undefined => {
    const name = row[`${prefix}name`];
    if (!name) return undefined;

    return {
      name,
      species: row[`${prefix}species`] || 'Dog',
      breed: row[`${prefix}breed`] || undefined,
      color: row[`${prefix}color`] || undefined,
      gender: row[`${prefix}gender`] || undefined,
      date_of_birth: row[`${prefix}date_of_birth`] || undefined,
      weight: row[`${prefix}weight`] ? parseFloat(row[`${prefix}weight`]) : undefined,
      microchip_id: row[`${prefix}microchip_id`] || undefined,
      spayed_neutered: row[`${prefix}spayed_neutered`]?.toLowerCase() === 'true' || row[`${prefix}spayed_neutered`] === '1',
      allergies: row[`${prefix}allergies`] || undefined,
      medications: row[`${prefix}medications`] || undefined,
    };
  };

  // Process uploaded file
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        
        if (rows.length < 2) {
          setError('CSV file must have a header row and at least one data row');
          return;
        }

        // First row is header
        const headers = rows[0].map(h => h.toLowerCase().replace(/\s+/g, '_'));
        const dataRows = rows.slice(1);

        const parsed: ParsedRow[] = dataRows.map((row, index) => {
          const rowData: Record<string, string> = {};
          headers.forEach((header, i) => {
            rowData[header] = row[i] || '';
          });

          const errors: string[] = [];
          
          // Validate required fields
          if (!rowData.name?.trim()) {
            errors.push('Name is required');
          }

          // Build import record
          const record: ImportRecord = {
            name: rowData.name?.trim() || '',
            email: rowData.email?.trim() || undefined,
            phone: rowData.phone ? normalizePhone(rowData.phone.trim()) : undefined,
            address: rowData.address?.trim() || undefined,
            tracking_code: rowData.tracking_code ? parseInt(rowData.tracking_code, 10) : undefined,
            pet1: buildPetData(rowData, 'pet1_'),
            pet2: buildPetData(rowData, 'pet2_'),
            pet3: buildPetData(rowData, 'pet3_'),
          };

          // Validate tracking_code is a valid number
          if (rowData.tracking_code && isNaN(record.tracking_code!)) {
            errors.push('Tracking code must be a number');
            record.tracking_code = undefined;
          }

          return {
            rowNumber: index + 2, // +2 because row 1 is header, and we're 0-indexed
            data: record,
            errors,
            isValid: errors.length === 0,
          };
        });

        setParsedRows(parsed);
        setStep('preview');
        setError(null);
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsText(file);
  }, []);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  // Handle file select
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  // Import records
  const handleImport = async () => {
    const validRows = parsedRows.filter(r => r.isValid);
    if (validRows.length === 0) {
      setError('No valid records to import');
      return;
    }

    setStep('importing');
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.PET_OWNERS.BULK_IMPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          records: validRows.map(r => r.data),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Import failed');
      }

      const result: ImportResult = await response.json();
      setImportResult(result);
      setStep('results');

      if (result.successful > 0) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('preview');
    }
  };

  // Download sample CSV
  const downloadSample = () => {
    const headers = [
      'name', 'email', 'phone', 'address', 'tracking_code',
      'pet1_name', 'pet1_species', 'pet1_breed', 'pet1_color', 'pet1_gender', 'pet1_date_of_birth', 'pet1_weight', 'pet1_microchip_id', 'pet1_spayed_neutered', 'pet1_allergies', 'pet1_medications',
      'pet2_name', 'pet2_species', 'pet2_breed',
      'pet3_name', 'pet3_species', 'pet3_breed'
    ];
    const sampleRow = [
      'John Smith', 'john@example.com', '4157549214', '123 Main St, San Francisco CA 94102', '12345',
      'Buddy', 'Dog', 'Golden Retriever', 'Golden', 'Male', '2020-05-15', '65', 'ABC123456', 'true', 'None known', 'Heartworm prevention',
      'Whiskers', 'Cat', 'Tabby',
      '', '', ''
    ];

    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pet_owners_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const validCount = parsedRows.filter(r => r.isValid).length;
  const invalidCount = parsedRows.filter(r => !r.isValid).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">
            Import Pet Owners from CSV
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-secondary rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-6">
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all
                  ${isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-dark-border hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                  }
                `}
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="text-lg font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Drop your CSV file here
                </p>
                <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                  or click to browse
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Sample download */}
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={downloadSample}
                  className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Download className="w-4 h-4" />
                  Download sample CSV template
                </button>
              </div>

              {/* Format info */}
              <div className="bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-dark-text-primary mb-2">
                  CSV Format
                </h3>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-3">
                  Required columns: <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">name</code>
                </p>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                  Optional columns:
                </p>
                <ul className="text-sm text-gray-500 dark:text-dark-text-muted space-y-1 ml-4">
                  <li>• <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">email</code>, <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">phone</code>, <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">address</code>, <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">tracking_code</code></li>
                  <li>• Pet 1: <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">pet1_name</code>, <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">pet1_species</code>, <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">pet1_breed</code>, etc.</li>
                  <li>• Pet 2 & 3: Same pattern with <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">pet2_</code> and <code className="bg-gray-200 dark:bg-dark-bg-secondary px-1 rounded">pet3_</code> prefixes</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-dark-text-secondary">{validCount} valid records</span>
                </div>
                {invalidCount > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-600 dark:text-red-400">{invalidCount} with errors</span>
                  </div>
                )}
              </div>

              {/* Preview table */}
              <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                    <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Row</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Tracking</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Pets</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
                      {parsedRows.map((row) => (
                        <tr key={row.rowNumber} className={!row.isValid ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-dark-text-muted">{row.rowNumber}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-dark-text-primary font-medium">{row.data.name || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">{row.data.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">{row.data.phone || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">{row.data.tracking_code || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">
                            {[row.data.pet1, row.data.pet2, row.data.pet3].filter(Boolean).length || '-'}
                          </td>
                          <td className="px-4 py-3">
                            {row.isValid ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="w-3 h-3" /> Valid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title={row.errors.join(', ')}>
                                <AlertCircle className="w-3 h-3" /> {row.errors[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-lg font-medium text-gray-700 dark:text-dark-text-secondary">
                Importing {validCount} records...
              </p>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-2">
                Please wait, this may take a moment
              </p>
            </div>
          )}

          {step === 'results' && importResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{importResult.total_records}</p>
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted">Total Records</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{importResult.successful}</p>
                  <p className="text-sm text-green-600 dark:text-green-400">Imported</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">{importResult.failed}</p>
                  <p className="text-sm text-red-600 dark:text-red-400">Failed</p>
                </div>
              </div>

              {/* Results table */}
              <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-64">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                    <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Row</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Pets Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase">Result</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
                      {importResult.results.map((result) => (
                        <tr key={result.row_number} className={!result.success ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-dark-text-muted">{result.row_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-dark-text-primary font-medium">{result.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">{result.pets_created}</td>
                          <td className="px-4 py-3">
                            {result.success ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="w-3 h-3" /> Imported
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title={result.error}>
                                <AlertCircle className="w-3 h-3" /> {result.error}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary">
          {step === 'upload' && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-text-muted">
              <FileText className="w-4 h-4" />
              Supported format: CSV
            </div>
          )}
          {step === 'preview' && (
            <button
              onClick={() => setStep('upload')}
              className="text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-800 dark:hover:text-dark-text-primary"
            >
              ← Upload different file
            </button>
          )}
          {step === 'results' && (
            <div />
          )}
          {step === 'importing' && <div />}

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
            >
              {step === 'results' ? 'Close' : 'Cancel'}
            </button>
            {step === 'preview' && (
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Import {validCount} Record{validCount !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
